import { createContext, useContext, useState, useCallback } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) throw new Error('useLanguage must be used within LanguageProvider');
  return context;
};

const translations = {
  es: {
    // Nav
    nav_inicio: 'Inicio',
    nav_menu: 'Menu',
    nav_galeria: 'Galeria',
    nav_resenas: 'Resenas',
    nav_contacto: 'Contacto',
    // Hero
    hero_overline: 'Autentica Cocina India',
    hero_title_1: 'Sabores que Cuentan',
    hero_title_2: 'Historias',
    hero_desc: 'Descubre los sabores milenarios de la India con nuestros platos preparados con ingredientes frescos y especias tradicionales.',
    hero_btn_menu: 'Ver Menu y Pedir',
    hero_btn_order: 'Pedir Ahora',
    // Menu
    menu_overline: 'Nuestros Platos',
    menu_title: 'Explora Nuestro Menu',
    menu_desc: 'Cada plato es una obra maestra culinaria, preparado con amor y las mejores especias de la India.',
    menu_all: 'Todos',
    menu_add: 'Anadir al Carrito',
    menu_unavailable: 'Pedidos no disponibles',
    menu_spicy: 'Picante',
    menu_veg: 'Veg',
    // Gallery
    gallery_overline: 'Nuestra Galeria',
    gallery_title: 'Un Festin Visual',
    gallery_desc: 'Descubre la belleza de nuestros platos y el ambiente acogedor de nuestro restaurante.',
    // Testimonials
    test_overline: 'Testimonios',
    test_title: 'Lo Que Dicen Nuestros Clientes',
    test_desc: 'La satisfaccion de nuestros clientes es nuestra mayor recompensa.',
    // Contact
    contact_overline: 'Contactanos',
    contact_title: 'Visitanos o Haz Tu Pedido',
    contact_desc: 'Estamos aqui para atenderte. Ven a disfrutar de una experiencia gastronomica unica o realiza tu pedido online.',
    contact_address: 'Direccion',
    contact_phone: 'Telefono',
    contact_email: 'Email',
    contact_hours: 'Horario',
    contact_hours_value: 'Lun-Dom: 12:00 - 23:00',
    contact_location: 'Nuestra Ubicacion',
    contact_map: 'Ver en Google Maps',
    // Footer
    footer_desc: 'Autentica cocina india en San Fernando, Cadiz. Sabores tradicionales con un toque moderno.',
    footer_links: 'Enlaces Rapidos',
    footer_legal: 'Horario y Legal',
    footer_privacy: 'Politica de Privacidad',
    footer_terms: 'Terminos y Condiciones',
    footer_rights: 'Todos los derechos reservados.',
    footer_weekday: 'Lunes - Viernes',
    footer_weekend: 'Sabado - Domingo',
    // Cart
    cart_title: 'Tu Carrito',
    cart_empty: 'Tu carrito esta vacio',
    cart_empty_desc: 'Anade algunos deliciosos platos para comenzar',
    cart_view_menu: 'Ver Menu',
    cart_total: 'Total',
    cart_checkout: 'Finalizar Pedido',
    // Checkout
    checkout_title: 'Finalizar Pedido',
    checkout_back: 'Volver al Menu',
    checkout_type: 'Tipo de Pedido',
    checkout_pickup: 'Recogida',
    checkout_delivery: 'Entrega',
    checkout_customer: 'Datos del Cliente',
    checkout_name: 'Nombre *',
    checkout_name_ph: 'Tu nombre completo',
    checkout_phone: 'Telefono *',
    checkout_phone_ph: '+34 600 000 000',
    checkout_address: 'Direccion de entrega *',
    checkout_address_ph: 'Calle, numero, piso...',
    checkout_notes: 'Notas (opcional)',
    checkout_notes_ph: 'Alergias, instrucciones especiales...',
    checkout_summary: 'Resumen del Pedido',
    checkout_subtotal: 'Subtotal',
    checkout_delivery_fee: 'Envio a domicilio',
    checkout_free: 'Gratis',
    checkout_pickup_label: 'Recogida',
    checkout_total: 'Total',
    checkout_time: 'Tiempo estimado',
    checkout_pay_online: 'Pagar Online',
    checkout_pay_whatsapp: 'Pedir por WhatsApp',
    // Order tracking
    order_title: 'Seguimiento de Pedido',
    order_number: 'Pedido',
    order_status: 'Estado',
    order_placed: 'Pedido Realizado',
    order_confirmed: 'Confirmado',
    order_preparing: 'Preparando',
    order_ready: 'Listo',
    order_delivered: 'Entregado',
    order_items: 'Articulos',
    order_est_time: 'Tiempo estimado',
    order_back: 'Volver al Inicio',
    // Spiciness
    spice_none: 'Sin Picante',
    spice_mild: 'Poco Picante',
    spice_medium: 'Medio Picante',
    spice_hot: 'Muy Picante',
    // Cookie
    cookie_text: 'Utilizamos cookies tecnicas necesarias para el funcionamiento del carrito de compra y su sesion. No utilizamos cookies de seguimiento ni publicitarias.',
    cookie_more: 'Mas informacion en nuestra',
    cookie_policy: 'Politica de Privacidad',
    cookie_accept: 'Aceptar',
    cookie_reject: 'Rechazar',
  },
  en: {
    // Nav
    nav_inicio: 'Home',
    nav_menu: 'Menu',
    nav_galeria: 'Gallery',
    nav_resenas: 'Reviews',
    nav_contacto: 'Contact',
    // Hero
    hero_overline: 'Authentic Indian Cuisine',
    hero_title_1: 'Flavours that Tell',
    hero_title_2: 'Stories',
    hero_desc: 'Discover the ancient flavours of India with our dishes prepared with fresh ingredients and traditional spices.',
    hero_btn_menu: 'View Menu & Order',
    hero_btn_order: 'Order Now',
    // Menu
    menu_overline: 'Our Dishes',
    menu_title: 'Explore Our Menu',
    menu_desc: 'Each dish is a culinary masterpiece, prepared with love and the finest spices from India.',
    menu_all: 'All',
    menu_add: 'Add to Cart',
    menu_unavailable: 'Orders unavailable',
    menu_spicy: 'Spicy',
    menu_veg: 'Veg',
    // Gallery
    gallery_overline: 'Our Gallery',
    gallery_title: 'A Visual Feast',
    gallery_desc: 'Discover the beauty of our dishes and the cosy atmosphere of our restaurant.',
    // Testimonials
    test_overline: 'Testimonials',
    test_title: 'What Our Customers Say',
    test_desc: 'Our customers\' satisfaction is our greatest reward.',
    // Contact
    contact_overline: 'Contact Us',
    contact_title: 'Visit Us or Place Your Order',
    contact_desc: 'We are here to serve you. Come enjoy a unique dining experience or place your order online.',
    contact_address: 'Address',
    contact_phone: 'Phone',
    contact_email: 'Email',
    contact_hours: 'Hours',
    contact_hours_value: 'Mon-Sun: 12:00 - 23:00',
    contact_location: 'Our Location',
    contact_map: 'View on Google Maps',
    // Footer
    footer_desc: 'Authentic Indian cuisine in San Fernando, Cadiz. Traditional flavours with a modern twist.',
    footer_links: 'Quick Links',
    footer_legal: 'Hours & Legal',
    footer_privacy: 'Privacy Policy',
    footer_terms: 'Terms & Conditions',
    footer_rights: 'All rights reserved.',
    footer_weekday: 'Monday - Friday',
    footer_weekend: 'Saturday - Sunday',
    // Cart
    cart_title: 'Your Cart',
    cart_empty: 'Your cart is empty',
    cart_empty_desc: 'Add some delicious dishes to get started',
    cart_view_menu: 'View Menu',
    cart_total: 'Total',
    cart_checkout: 'Proceed to Checkout',
    // Checkout
    checkout_title: 'Checkout',
    checkout_back: 'Back to Menu',
    checkout_type: 'Order Type',
    checkout_pickup: 'Pickup',
    checkout_delivery: 'Delivery',
    checkout_customer: 'Customer Details',
    checkout_name: 'Name *',
    checkout_name_ph: 'Your full name',
    checkout_phone: 'Phone *',
    checkout_phone_ph: '+34 600 000 000',
    checkout_address: 'Delivery address *',
    checkout_address_ph: 'Street, number, floor...',
    checkout_notes: 'Notes (optional)',
    checkout_notes_ph: 'Allergies, special instructions...',
    checkout_summary: 'Order Summary',
    checkout_subtotal: 'Subtotal',
    checkout_delivery_fee: 'Delivery fee',
    checkout_free: 'Free',
    checkout_pickup_label: 'Pickup',
    checkout_total: 'Total',
    checkout_time: 'Estimated time',
    checkout_pay_online: 'Pay Online',
    checkout_pay_whatsapp: 'Order via WhatsApp',
    // Order tracking
    order_title: 'Order Tracking',
    order_number: 'Order',
    order_status: 'Status',
    order_placed: 'Order Placed',
    order_confirmed: 'Confirmed',
    order_preparing: 'Preparing',
    order_ready: 'Ready',
    order_delivered: 'Delivered',
    order_items: 'Items',
    order_est_time: 'Estimated time',
    order_back: 'Back to Home',
    // Spiciness
    spice_none: 'Not Spicy',
    spice_mild: 'Mild',
    spice_medium: 'Medium Spicy',
    spice_hot: 'Very Spicy',
    // Cookie
    cookie_text: 'We use essential cookies for the shopping cart and your session. We do not use tracking or advertising cookies.',
    cookie_more: 'More information in our',
    cookie_policy: 'Privacy Policy',
    cookie_accept: 'Accept',
    cookie_reject: 'Reject',
  }
};

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState(() => localStorage.getItem('lang') || 'es');

  const toggleLang = useCallback(() => {
    setLang(prev => {
      const next = prev === 'es' ? 'en' : 'es';
      localStorage.setItem('lang', next);
      return next;
    });
  }, []);

  const t = useCallback((key) => translations[lang]?.[key] || translations['es']?.[key] || key, [lang]);

  return (
    <LanguageContext.Provider value={{ lang, toggleLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
};
